import { useMemo } from 'react';

import { FormikValues, useFormik } from 'formik';
import { DateTime } from 'luxon';

import {
  AdminAllowanceNames,
  adminCreateShiftContract,
  adminPharmaciesInfo,
  AllowanceEnums,
  AllowancesNames,
  AllowanceTypesToHandle,
  ContractTypes,
  convertToDecimals,
  createWorkshiftAdmin,
  getRecurrence,
  ProfessionalReplacementTypes,
  TravelAllowanceEnums,
} from '@pharmaplan/common';

import { handleWorkshiftToggles } from '@pharmaplan/common/helpers/utils';
import { makeReqParams } from '../../../helpers/workshift/functions';
import { DurationTypes } from '../../../helpers/Constants';
import { useAppSelector } from '../../useAppSelector';
import { useAppDispatch } from '../../useAppDispatch';
import { AdminWorkshiftValidator } from '../../../helpers/commonValidators';
import { parseEmptyStringProxy, parseFloatProxy } from '../../../helpers/proxyHandlers';
import useSyncWorkshiftValues from '../../workshift/useSyncWorkshiftValues';
import { rateValidation } from '../../../helpers/Functions';
import useSelfService from '../../useSelfService';
import useGetChangedContract from '../../LongTermContract/useGetChangedContract';

const { notRepeat } = DurationTypes;
const { accommodation, meal, emergency, others } = AllowanceEnums;
const {
  travelAllowanceType,
  accommodationAllowanceType,
  paidHourlyRate: paidHourlyRateEnum,
  accommodationAllowance,
  mealAllowance,
  travelAllowanceAmount,
  travelAllowanceRatePerKm,
  emergencyFees,
  otherAllowance,
  weekDayPaidHourlyRate,
  weekendPaidHourlyRate,
} = AllowancesNames;

const { pharmacyWeekDayHourlyRate, pharmacyWeekendHourlyRate, pharmacyHourlyRate: pharmacyHourlyRateEnum } = AdminAllowanceNames;

interface IUseAdminCreateWorkshift {
  pharmacyId: string;
  name: string;
}

const useAdminCreateWorkshift = ({
  pharmacyId,
  name,
}: IUseAdminCreateWorkshift) => {
  const dispatch = useAppDispatch();

  const { isSelfService } = useSelfService();
  const { getChangedItems, comparator } = useGetChangedContract();

  const info = useAppSelector(adminPharmaciesInfo);
  const { hourlyRate, pharmacyHourlyRate } = info ?? {};

  const recurrence = useAppSelector(getRecurrence);
  const today = useMemo(() =>
    DateTime.now(), []);

  const onSubmit = (values: FormikValues) => {
    const {
      notes,
      pharmacyHourlyRate: formikPharmacyHourlyRate,
      paidHourlyRate,
      weekendPaidHourlyRate: weekRate,
      weekDayPaidHourlyRate: weekendRate,
      pharmacyWeekDayHourlyRate: pharmacyWeekdayRate,
      pharmacyWeekendHourlyRate: pharmacyWeekendRate,
    } = values ?? {};

    if (values?.contractType === ContractTypes.longTerm) {
      const { startDate, endDate } = comparator();

      const dateParams = {
        startDate,
        endDate,
      };

      const rates = {
        weekendPaidHourlyRate: weekRate,
        weekDayPaidHourlyRate: weekendRate,
        pharmacyWeekDayHourlyRate: pharmacyWeekdayRate,
        pharmacyWeekendHourlyRate: pharmacyWeekendRate,
      };

      const handledForEmptyString = new Proxy(rates, parseEmptyStringProxy);
      const handledForFloat = new Proxy(handledForEmptyString, parseFloatProxy);

      dispatch(
        adminCreateShiftContract({
          pharmacyId,
          model: {
            ...makeReqParams(values, recurrence, dateParams),
            allowanceNotes: values?.allowanceNotes,
            isLongTermContract: values?.contractType === ContractTypes.longTerm,
            shiftContractAttributes: getChangedItems(),
            typeReplacement: values?.replacementType,
            ...handledForFloat,
          },
        }),
      );
      return;
    }

    const val = {
      travelAllowanceRatePerKm: handleWorkshiftToggles(
        values[travelAllowanceType]?.includes(TravelAllowanceEnums.valueKm),
        values[travelAllowanceRatePerKm],
        null,
      ),
      travelAllowanceAmount: handleWorkshiftToggles(
        values[travelAllowanceType].includes(TravelAllowanceEnums.travelAmount),
        values[travelAllowanceAmount],
        null,
      ),
      mealAllowance: handleWorkshiftToggles(
        values[meal],
        values[mealAllowance],
        null,
      ),
      accommodationAllowance: handleWorkshiftToggles(
        values[accommodationAllowanceType]
          === AllowanceTypesToHandle.paidValuePerNight,
        values[accommodationAllowance],
        null,
      ),
      paidHourlyRate: values[paidHourlyRateEnum],
      emergencyFees: handleWorkshiftToggles(
        values[emergency],
        values[emergencyFees],
        null,
      ),
      pharmacyHourlyRate: values[pharmacyHourlyRateEnum],
      otherAllowance: handleWorkshiftToggles(
        values[others],
        values[otherAllowance],
        null,
      ),
    };

    const handledForDecimals = new Proxy(val, parseFloatProxy);

    const isPharmacyGreaterPharmacist = rateValidation({
      pharmacistRate: paidHourlyRate,
      pharmacyRate: formikPharmacyHourlyRate,
      isSelfService,
      dispatch,
    });

    if (isPharmacyGreaterPharmacist) {
      dispatch(
        createWorkshiftAdmin({
          pharmacyId,
          model: {
            travelAllowanceTypes: values[travelAllowanceType],
            accommodationAllowanceType: handleWorkshiftToggles(
              values[accommodation],
              values[accommodationAllowanceType],
              null,
            ),
            ...makeReqParams(values, recurrence),
            ...handledForDecimals,
            note: notes,
          },
        }),
      );
    }
  };

  const initialValues = {
    name,
    selectedDate: today,
    recurrence: notRepeat,
    startTime: null,
    endTime: null,
    notes: '',
    emergencyFees: '',
    mealAllowance: '',
    travelAllowance: '',
    accommodationAllowance: '',
    paidHourlyRate: convertToDecimals(hourlyRate),
    pharmacyHourlyRate: convertToDecimals(pharmacyHourlyRate),
    contractType: ContractTypes.shortTerm,
    replacementType: ProfessionalReplacementTypes.pharmacist,

    [travelAllowanceType]: [],
    [accommodationAllowanceType]: '',
    [weekDayPaidHourlyRate]: '',
    [weekendPaidHourlyRate]: '',
    [pharmacyWeekDayHourlyRate]: '',
    [pharmacyWeekendHourlyRate]: '',
    [travelAllowanceRatePerKm]: '',
    [travelAllowanceAmount]: '',
    [accommodationAllowance]: '',
    [mealAllowance]: '',
    [emergencyFees]: '',
    [otherAllowance]: '',
  };

  const formik = useFormik({
    initialValues,
    validationSchema: AdminWorkshiftValidator(),
    enableReinitialize: true,
    onSubmit,
  });

  useSyncWorkshiftValues(formik);

  return { formik };
};

export default useAdminCreateWorkshift;
